import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Users,
  BedDouble,
  AirVent,
  Waves,
  Globe,
  Clock,
  MessageCircle,
  Facebook,
  Phone,
  Share2
} from 'lucide-react';
import { propertyImages } from '../assets/images';

const TRANSLATIONS = {
  es: {
    nav: ['Portafolio', 'Experiencia', 'Nosotros', 'Revista'],
    explore: 'Desliza para Explorar',
    baseRate: 'Tarifa Base',
    extraPersonRate: 'Tarifa Persona Extra',
    perNight: 'USD / Noche',
    taxesIncluded: 'Incluye todos los impuestos',
    cleaningFee: 'Tarifa de limpieza: $25 (no incluida)',
    narrative: 'La Narrativa',
    physicalAddress: 'Dirección Física',
    navigation: 'Navegación',
    specs: 'Especificaciones Vitales',
    guests: 'Hasta {n} Huéspedes',
    bedrooms: '{n} Habitaciones',
    airConditioners: '{n} Aire Acondicionado',
    poolPrivate: 'Piscina Privada',
    poolCommon: 'Piscina Común',
    noPool: 'Sin Acceso a Piscina',
    checkInOut: 'Entrada: {checkIn} | Salida: {checkOut}',
    contact: 'Contacto',
    inquiries: 'Consultas',
    partnerships: 'Alianzas',
    privacy: 'Privacidad',
    legal: 'Legal',
    lowSeason: 'Temporada Baja',
    peakSeason: 'Temporada Alta',
    selectSeason: 'Selecciona Temporada',
    note: 'Nota: 3 habitaciones para huéspedes normales, 4 habitaciones si se requieren personas extra.',
    callRep: 'Llamar al representante',
    shareWhatsApp: 'Compartir por WhatsApp',
    shareFacebook: 'Compartir en Facebook',
    copyLink: 'Copiar enlace',
    linkCopied: '¡Enlace copiado!',
    share: 'Compartir Propiedad',
    seasonToggle: 'Tarifas por Temporada'
  },
  en: {
    nav: ['Portfolio', 'Experience', 'About', 'Journal'],
    explore: 'Scroll to Explore',
    baseRate: 'Base Rate',
    extraPersonRate: 'Extra Person Rate',
    perNight: 'USD / Night',
    taxesIncluded: 'All taxes included',
    cleaningFee: 'Cleaning fee: $25 (not included)',
    narrative: 'The Narrative',
    physicalAddress: 'Physical Address',
    navigation: 'Navigation',
    specs: 'Vital Specs',
    guests: 'Up to {n} Guests',
    bedrooms: '{n} Bedrooms',
    airConditioners: '{n} Air Conditioners',
    poolPrivate: 'Private Pool',
    poolCommon: 'Common Pool',
    noPool: 'No Pool Access',
    checkInOut: 'Check-in: {checkIn} | Check-out: {checkOut}',
    contact: 'Contact',
    inquiries: 'Inquiries',
    partnerships: 'Partnerships',
    privacy: 'Privacy',
    legal: 'Legal',
    lowSeason: 'Low Season',
    peakSeason: 'Peak Season',
    selectSeason: 'Select Season',
    note: 'Note: 3 bedrooms for normal guests, 4 bedrooms if extra persons are required.',
    callRep: 'Call Representative',
    shareWhatsApp: 'Share via WhatsApp',
    shareFacebook: 'Share on Facebook',
    copyLink: 'Copy Link',
    linkCopied: 'Link copied!',
    share: 'Share Property',
    seasonToggle: 'Seasonal Rates'
  }
};

const MOCK_PROPERTIES = [
  {
    id: 1,
    hidden: false,
    imageKey: 'obsidianMonolith',
    price: {
      lowSeason: 300,
      peakSeason: 400
    },
    extraPersonRate: 25,
    guests: 14,
    bedrooms: {
      normal: 3,
      withExtra: 4
    },
    airConditioners: 4,
    poolAccess: "private",
    checkIn: "3:00 PM",
    checkOut: "12:00 PM",
    phone: "+50370000001",
    address: "Playa el Amatal, La Libertad 15 minutos adelante de playa San Diego",
    mapsUrl: "https://www.google.com/maps?q=Playa+el+Amatal+La+Libertad",
    wazeUrl: "https://waze.com/ul?q=Playa+el+Amatal+La+Libertad&navigate=yes",
    content: {
      en: {
        title: "Altamar Beach House",
        location: "Playa el Amatal, La Libertad",
        tagline: "Exclusive beachfront retreat",
        description: "Experience the ultimate beachfront luxury at Casa de Playa Altamar. Located in the beautiful Playa el Amatal, just 15 minutes past San Diego beach, this stunning property offers private pool access, air conditioning, and ample space for up to 14 guests. It comes with 3 standard bedrooms, and an optional 4th bedroom when extra guests are accommodated. Enjoy breathtaking ocean views, direct beach access, and a fully equipped layout designed for comfort and relaxation."
      },
      es: {
        title: "Casa de Playa Altamar",
        location: "Playa el Amatal, La Libertad",
        tagline: "Retiro exclusivo frente al mar",
        description: "Experimente el máximo lujo frente al mar en Casa de Playa Altamar. Ubicada en la hermosa Playa el Amatal, a solo 15 minutos de la playa de San Diego, esta impresionante propiedad ofrece acceso a piscina privada, aire acondicionado y amplio espacio para hasta 14 huéspedes. Cuenta con 3 habitaciones estándar y una cuarta habitación opcional cuando se alojan personas adicionales. Disfrute de impresionantes vistas al océano, acceso directo a la playa y un diseño totalmente equipado pensado para la comodidad y la relajación."
      }
    },
    images: propertyImages.obsidianMonolith
  }
];

const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const nextSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const selectImage = (index) => {
    setCurrentIndex(index);
  };

  const scrollThumbnails = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Main Image - Full Width */}
      <div className="relative group aspect-[16/9] overflow-hidden bg-gray-100 rounded-sm shadow-xl">
        <img
          src={images[currentIndex]}
          alt={`${title} - ${currentIndex + 1}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Navigation Arrows */}
        <div className="absolute inset-0 flex items-center justify-between px-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <button onClick={prevSlide} className="p-3 bg-white/10 backdrop-blur-md text-white rounded-full hover:bg-white/30 border border-white/20 transition-all">
            <ChevronLeft size={20} />
          </button>
          <button onClick={nextSlide} className="p-3 bg-white/10 backdrop-blur-md text-white rounded-full hover:bg-white/30 border border-white/20 transition-all">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Image Counter */}
        <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnail Collage - Below Main Image - Horizontal Scroll */}
      <div className="relative">
        {/* Scroll Left Button */}
        <button
          onClick={() => scrollThumbnails('left')}
          className="absolute left-0 top-0 bottom-0 z-10 bg-gradient-to-r from-white to-transparent px-3 flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
        >
          <div className="bg-slate-900 text-white p-2 rounded-full shadow-lg">
            <ChevronLeft size={20} />
          </div>
        </button>

        {/* Scrollable Thumbnail Container - Horizontal */}
        <div
          ref={scrollContainerRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide px-12 py-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {images.map((img, idx) => (
            <div
              key={idx}
              onClick={() => selectImage(idx)}
              className={`relative flex-shrink-0 w-32 h-32 overflow-hidden bg-gray-100 rounded-sm cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                currentIndex === idx ? 'ring-2 ring-slate-900 ring-offset-2' : ''
              }`}
            >
              <img
                src={img}
                alt={`${title} thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Image number overlay */}
              <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 rounded text-xs font-medium">
                {idx + 1}
              </div>
            </div>
          ))}
        </div>

        {/* Scroll Right Button */}
        <button
          onClick={() => scrollThumbnails('right')}
          className="absolute right-0 top-0 bottom-0 z-10 bg-gradient-to-l from-white to-transparent px-3 flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
        >
          <div className="bg-slate-900 text-white p-2 rounded-full shadow-lg">
            <ChevronRight size={20} />
          </div>
        </button>
      </div>
    </div>
  );
};

const LookbookCard = ({ property, lang }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState('low');
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const t = TRANSLATIONS[lang];
  const content = property.content[lang];

  const currentPrice = selectedSeason === 'low' 
    ? property.price.lowSeason 
    : property.price.peakSeason;

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  const buildShareText = () => {
    const priceText = selectedSeason === 'low' 
      ? `$${property.price.lowSeason}/noche (${t.lowSeason})`
      : `$${property.price.peakSeason}/noche (${t.peakSeason})`;
    return `${content.title} • ${content.location}\n${priceText}\n${t.guests.replace('{n}', property.guests)}\nVer más: ${window.location.href}`;
  };

  const shareText = encodeURIComponent(buildShareText());
  const currentUrl = encodeURIComponent(window.location.href);
  const whatsappUrl = `https://wa.me/?text=${shareText}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert("No se pudo copiar el enlace.");
    }
  };

  return (
    <article 
      ref={cardRef}
      className={`max-w-5xl mx-auto mb-40 transition-all duration-1000 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      <ImageCarousel images={property.images} title={content.title} />

      <div className="mt-12 px-6 sm:px-0">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-100 pb-10 mb-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-slate-400 uppercase tracking-[0.3em] text-[10px] font-bold mb-3">
              <MapPin size={12} className="text-slate-300" />
              {content.location}
            </div>
            <h2 className="text-4xl md:text-6xl font-serif text-slate-900 leading-tight">
              {content.title}
            </h2>
            <p className="mt-4 text-xl font-light text-slate-500 italic font-serif">
              "{content.tagline}"
            </p>
          </div>
          
          <div className="text-right md:min-w-[240px]">
            {/* Season Selector inside pricing */}
            <div className="mb-6 flex flex-col items-end border-b border-slate-100 pb-4">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-3">{t.seasonToggle}</span>
              <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-full">
                <button
                  onClick={() => setSelectedSeason('low')}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                    selectedSeason === 'low' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {lang === 'es' ? 'Baja' : 'Low'}
                </button>
                <button
                  onClick={() => setSelectedSeason('peak')}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                    selectedSeason === 'peak' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {lang === 'es' ? 'Alta' : 'Peak'}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">
                {t.baseRate} ({selectedSeason === 'low' ? t.lowSeason : t.peakSeason})
              </span>
              <span className="text-4xl font-serif text-slate-900">${currentPrice}</span>
              <span className="text-slate-400 ml-2 text-sm font-medium">{t.perNight}</span>
            </div>
            {property.extraPersonRate && (
              <div className="pt-3 border-t border-slate-100 mb-4">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">{t.extraPersonRate}</span>
                <span className="text-2xl font-serif text-slate-900">${property.extraPersonRate}</span>
                <span className="text-slate-400 ml-2 text-sm font-medium">{t.perNight}</span>
              </div>
            )}
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <p className="text-xs text-slate-500 italic leading-relaxed">
                {t.taxesIncluded}
              </p>
              <p className="text-xs text-slate-500 italic leading-relaxed">
                {t.cleaningFee}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8">
            <div className="mb-12">
              <h3 className="text-[11px] uppercase tracking-[0.3em] font-bold text-slate-400 mb-6">{t.narrative}</h3>
              <p className="text-slate-600 text-lg leading-relaxed font-light mb-12">
                {content.description}
              </p>
              
              <div className="bg-slate-50 border border-slate-100 rounded-sm p-6 mb-12 italic text-sm text-slate-600 flex gap-4 items-start">
                <div className="w-1 bg-slate-400 self-stretch rounded-full" />
                <p>{t.note}</p>
              </div>

              <div className="pt-10 border-t border-slate-100">
                <div className="mb-8">
                  <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400 block mb-3">{t.physicalAddress}</span>
                  <p className="text-slate-900 font-serif text-2xl leading-snug">
                    {property.address}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-8">
                  <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400">{t.navigation}:</span>
                  <div className="flex items-center gap-10">
                    <a 
                      href={property.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[12px] uppercase tracking-widest font-bold text-slate-900 hover:text-slate-500 transition-all flex items-center gap-2 group"
                    >
                      Google Maps <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </a>
                    <a 
                      href={property.wazeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[12px] uppercase tracking-widest font-bold text-slate-900 hover:text-slate-500 transition-all flex items-center gap-2 group"
                    >
                      Waze <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 bg-slate-50 p-10 rounded-sm self-start">
            <h3 className="text-[11px] uppercase tracking-[0.3em] font-bold text-slate-900 mb-8 border-b border-slate-200 pb-4">{t.specs}</h3>
            <ul className="space-y-6 mb-12">
              <li className="flex items-center gap-5 text-slate-600">
                <Users size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">{t.guests.replace('{n}', property.guests)}</span>
              </li>
              <li className="flex items-center gap-5 text-slate-600">
                <BedDouble size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">{t.bedrooms.replace('{n}', `${property.bedrooms.normal}-${property.bedrooms.withExtra}`)}</span>
              </li>
              <li className="flex items-center gap-5 text-slate-600">
                <AirVent size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">{t.airConditioners.replace('{n}', property.airConditioners)}</span>
              </li>
              <li className="flex items-center gap-5 text-slate-600">
                <Waves size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">
                  {property.poolAccess === 'private' && t.poolPrivate}
                  {property.poolAccess === 'common' && t.poolCommon}
                  {property.poolAccess === 'none' && t.noPool}
                </span>
              </li>
              {property.checkIn && property.checkOut && (
                <li className="flex items-center gap-5 text-slate-600">
                  <Clock size={20} className="text-slate-400" />
                  <span className="text-sm tracking-wide">
                    {t.checkInOut.replace('{checkIn}', property.checkIn).replace('{checkOut}', property.checkOut)}
                  </span>
                </li>
              )}
            </ul>

            {/* Mobile Share Action Module */}
            <div className="border-t border-slate-200 pt-8">
              <h3 className="text-[11px] uppercase tracking-[0.3em] font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Share2 size={14} /> {t.share}
              </h3>
              
              <div className="space-y-3">
                <a
                  href={`tel:${property.phone}`}
                  className="flex items-center justify-center gap-3 w-full bg-slate-900 text-white rounded-sm py-3.5 px-4 text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors"
                >
                  <Phone size={16} /> {t.callRep}
                </a>

                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full bg-[#25D366] text-white rounded-sm py-3.5 px-4 text-xs font-bold uppercase tracking-widest hover:bg-[#128C7E] transition-colors"
                >
                  <MessageCircle size={16} /> {t.shareWhatsApp}
                </a>

                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full bg-[#1877F2] text-white rounded-sm py-3.5 px-4 text-xs font-bold uppercase tracking-widest hover:bg-[#166FE5] transition-colors"
                >
                  <Facebook size={16} /> {t.shareFacebook}
                </a>

                <button
                  onClick={handleCopy}
                  className="flex items-center justify-center gap-3 w-full border border-slate-300 text-slate-700 bg-white rounded-sm py-3.5 px-4 text-xs font-bold uppercase tracking-widest hover:bg-slate-50 transition-colors"
                >
                  {copied ? t.linkCopied : t.copyLink}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default function LookbookAltamar() {
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState('es');
  const { id } = useParams();
  const t = TRANSLATIONS[lang];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-sans text-slate-900 selection:bg-slate-900 selection:text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;600;700&display=swap');
        .font-serif { font-family: 'Playfair Display', serif; }
        .font-sans { font-family: 'Inter', sans-serif; }
        /* Hide scrollbar for Chrome, Safari and Opera */
        .scrollbar-hide::-webkit-scrollbar {
            display: none;
        }
        /* Hide scrollbar for IE, Edge and Firefox */
        .scrollbar-hide {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;  /* Firefox */
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 px-8 md:px-16 flex items-center justify-between ${
        scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-100 py-5 shadow-sm' : 'bg-transparent py-8'
      }`}>
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="w-10 h-10 bg-slate-900 flex items-center justify-center text-white font-serif text-2xl font-bold">M</div>
          <span className="text-[13px] tracking-[0.15em] font-bold text-slate-900 lowercase hidden sm:inline">
            mylookbook<span className="text-slate-400 font-light">.online</span>
          </span>
        </Link>

        <div className="flex items-center gap-6">
          {/* Language Switcher */}
          <div className="flex items-center gap-2 border border-slate-200 rounded-full px-3 py-1 bg-white/50 backdrop-blur-sm shadow-sm">
            <Globe size={14} className="text-slate-400" />
            <button
              onClick={() => setLang('es')}
              className={`text-[10px] font-bold tracking-widest ${lang === 'es' ? 'text-slate-900 underline underline-offset-4' : 'text-slate-400 hover:text-slate-600'}`}
            >
              ES
            </button>
            <span className="text-slate-200 text-[10px]">|</span>
            <button
              onClick={() => setLang('en')}
              className={`text-[10px] font-bold tracking-widest ${lang === 'en' ? 'text-slate-900 underline underline-offset-4' : 'text-slate-400 hover:text-slate-600'}`}
            >
              EN
            </button>
          </div>
        </div>
      </nav>

      {/* Spacing for fixed nav */}
      <div className="pt-32 pb-8"></div>

      {/* Main Feed */}
      <main className="px-4 sm:px-8 md:px-16">
        {MOCK_PROPERTIES.filter(prop => !prop.hidden).map((property) => (
          <LookbookCard key={property.id} property={property} lang={lang} />
        ))}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 sm:py-32 px-8 mt-10 sm:mt-20">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] uppercase tracking-[0.3em] font-bold text-slate-500">
          <span>&copy; 2026 mylookbook.online</span>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
            <span className="hover:text-white cursor-pointer transition-colors">{t.privacy}</span>
            <span className="hover:text-white cursor-pointer transition-colors">{t.legal}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Made with Bob
